import React from 'react'
import { useTranslation } from 'react-i18next'
import { FocusOverlay, useFocusSession } from '../features/focus'
import { useTaskOperations } from '../features/taskManagement/hooks/useTaskOperations'
import { isElectron } from '../lib/platform'

/**
 * Der Überzug selbst — getrennt, damit {@link FocusOverlayHost} unter Electron
 * **gar keine** Hooks anwirft: keine Live-Query, kein zweiter Schreiber des
 * Lebenszeichens, kein Ticken neben dem Fokus-Fenster.
 */
const RunningFocusOverlay: React.FC = () => {
  const { t } = useTranslation()
  const { task, card, complete } = useFocusSession()

  // `momentOrigin: 'focus'` — der Haken hier verdient den Geschafft-Moment
  // immer, ob eine Belohnung daranhängt oder nicht.
  const { handleToggleComplete } = useTaskOperations({
    t,
    tasks: task ? [task] : [],
    momentOrigin: 'focus',
  })

  if (!card) return null

  return <FocusOverlay card={{ ...card, onComplete: () => void complete(handleToggleComplete) }} />
}

/**
 * **Die Hülle des Fokus-Überzugs** — einmal in `MainLayout` gemountet, neben
 * dem {@link DoneMomentHost} (Kernschleife 3d).
 *
 * **Im Web** ist sie das, was auf dem Desktop `windows/FocusWindow` ist: Sie
 * verbindet das Fokus-Fenster mit dem Erledigen einer Aufgabe. Darum liegt sie hier und
 * nicht in `features/focus` — dort kommen zwei Features zusammen, und ein
 * Feature importiert kein anderes (CLAUDE.md, Dependency-Flow). Die Optik
 * bringt `FocusOverlay` mit, sie kennt nur Props.
 *
 * **Unter Electron zeichnet sie nie.** Dort hat das Fokus-Fenster ihr eigenes Fenster,
 * und ein Überzug im Hauptfenster deckte eine App zu, die gerade ohnehin
 * zurückgetreten ist. `isElectron()` steht für die Lebensdauer des Dokuments
 * fest — der Zweig ist also kein wechselndes Rendern, sondern eine Weiche, die
 * einmal fällt.
 *
 * **Der Geschafft-Moment kommt im selben Fenster:** `DoneMomentHost` hängt
 * bereits in `MainLayout`, und `requestDoneMoment` fällt ohne `window.api` von
 * selbst auf den lokalen Weg zurück. Hier ist dafür nichts zu tun.
 */
export const FocusOverlayHost: React.FC = () => (isElectron() ? null : <RunningFocusOverlay />)
